/**
 * ThumbnailOverlayBadge - Standardized overlay badges for StashThumbnail
 *
 * Provides consistent styling for status indicators that overlay thumbnails.
 * Designed to be composable - multiple badges can be stacked on a single thumbnail.
 *
 * Usage:
 * ```tsx
 * <ThumbnailOverlayBadge variant="processing" position="top-left" />
 * <ThumbnailOverlayBadge variant="tagged" position="top-right" />
 * ```
 *
 * Common use cases:
 * - Processing: Pipeline still enriching content
 * - Tagged: Item already has selected tags (tag edit mode)
 * - Selected: Item selected in multi-select mode
 * - Error: Pipeline processing failed
 */

import { type ReactNode } from "react";
import { Check, AlertTriangle, Loader2, Lock } from "lucide-react";
import { cn } from "@/lib/utils";

export type BadgeVariant = "processing" | "tagged" | "selected" | "error" | "restricted";
export type BadgePosition = "top-right" | "top-left" | "bottom-right" | "bottom-left";

export interface ThumbnailOverlayBadgeProps {
  /** Visual style variant */
  variant: BadgeVariant;
  /** Corner position on the thumbnail */
  position?: BadgePosition;
  /** Optional custom content (overrides default icon) */
  children?: ReactNode;
  /** Additional CSS classes */
  className?: string;
}

/**
 * Position classes for each corner
 */
const POSITION_CLASSES: Record<BadgePosition, string> = {
  "top-right": "top-2 right-2",
  "top-left": "top-2 left-2",
  "bottom-right": "bottom-2 right-2",
  "bottom-left": "bottom-2 left-2",
};

/**
 * Variant-specific styling
 * Each variant defines: background, text color, and default icon
 */
const VARIANT_CONFIG: Record<
  BadgeVariant,
  {
    containerClass: string;
    icon: ReactNode;
  }
> = {
  processing: {
    containerClass: "bg-amber-500/90 text-white",
    icon: <Loader2 className="h-3 w-3 animate-spin" />,
  },
  tagged: {
    containerClass: "bg-positive text-positive-foreground",
    icon: <Check className="h-3 w-3 stroke-[3]" />,
  },
  selected: {
    containerClass: "bg-primary text-primary-foreground",
    icon: <Check className="h-3 w-3 stroke-[3]" />,
  },
  error: {
    containerClass: "bg-destructive text-destructive-foreground",
    icon: <AlertTriangle className="h-3 w-3" />,
  },
  restricted: {
    containerClass: "bg-zinc-500/90 text-white",
    icon: <Lock className="h-3 w-3" />,
  },
};

/**
 * Renders a badge overlay for StashThumbnail
 *
 * The badge is absolutely positioned within the thumbnail container.
 * Parent must have `position: relative` for correct positioning.
 */
export function ThumbnailOverlayBadge({
  variant,
  position = "top-right",
  children,
  className,
}: ThumbnailOverlayBadgeProps) {
  const config = VARIANT_CONFIG[variant];
  const positionClass = POSITION_CLASSES[position];

  return (
    <div
      className={cn(
        // Base styles: positioning, sizing, shape
        "absolute z-10",
        "w-5 h-5 rounded-full",
        "flex items-center justify-center",
        // Visual polish
        "",
        // Position and variant
        positionClass,
        config.containerClass,
        className,
      )}
    >
      {children ?? config.icon}
    </div>
  );
}

ThumbnailOverlayBadge.displayName = "ThumbnailOverlayBadge";
